import { Toast } from "primereact/toast";
import React, { useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useToast } from "./Toast";
import { useTranslation } from "react-i18next";

// Define TypeScript type for menu items
interface MenuItem {
    label: string;
    to?: string;
    icon?: string;
    items?: MenuItem[];
}

const BreadcrumbComponent: React.FC = () => {
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { i18n } = useTranslation('common');


    // Retrieve and parse the menuTree from localStorage
    const clientState = localStorage.getItem("clientState");
    const clientStateJson = clientState ? JSON.parse(clientState) : {};
    const menuTree: MenuItem[] = clientState ? JSON.parse(clientState).menuTree : [];

    // Generate breadcrumbs based on the current path
    const breadcrumbs = generateBreadcrumbs(location.pathname, menuTree);

    if (clientState) {
        if (breadcrumbs.length === 0) {
            clientStateJson.error = clientStateJson.error || {};
            clientStateJson.error.detail = 'Access Denied';
            showToast(clientStateJson.error);
            navigate('/landing-page');
        }
    } else {
        navigate("/login");
    }

    return (
        <nav>
            <div style={{ display: "flex", alignItems: "center" }}>
                <Link to="/dashboard">
                    <span>Home</span>
                </Link>
                {breadcrumbs.map((item, index) => (
                    <React.Fragment key={index}>
                        {index !== 0 && <span style={{ margin: "0 5px" }}>&gt;</span>} {/* Add '>' only after the first item */}
                        {item.url ? (
                            <Link to={item.url}>{item.label}</Link> // Clickable links
                        ) : (
                            <span>{item.label}</span> // Non-clickable if the URL is null
                        )}
                    </React.Fragment>
                ))}
            </div>
        </nav>
    );
};

export default BreadcrumbComponent;

/**
 * Recursively searches for a path in the menu tree and returns the breadcrumb trail.
 */
const findPathInMenu = (
    menu: MenuItem[],
    path: string,
    parentPath = "",
    breadcrumbTrail: { label: string; url: string }[] = []
): { label: string; url: string }[] => {
    // Normalize the path (remove "-edit" or "-view" from the end of the last segment)
    const pathSegments = path.split("/").filter(Boolean);
    const lastSegment = pathSegments[1] || "";
    const normalizedPath = lastSegment.replace(/-(edit|view|upload)$/, ""); // Remove both "-edit" and "-view"

    for (const item of menu) {
        const currentPath = item.to ? `${item.to}` : parentPath;
        const currentPathSegments = currentPath.split("/").filter(Boolean);
        let lastCurrentPathSegment = currentPathSegments[1] || "";
        if (currentPathSegments[2] && pathSegments[2] !== currentPathSegments[2]) {
            lastCurrentPathSegment = "";
        }
        const normalizedCurrentPath = lastCurrentPathSegment.replace(/-(edit|view|upload)$/, ""); // Normalize

        // Compare normalized path without "-edit" or "-view"
        if (normalizedPath === normalizedCurrentPath) {
            if (path.includes("edit")) {
                return [
                    ...breadcrumbTrail,
                    { label: item.label, url: currentPath },
                    { label: `${item.label} Edit`, url: "" },
                ];
            }

            if (path.includes("view")) {
                return [
                    ...breadcrumbTrail,
                    { label: item.label, url: currentPath },
                    { label: `${item.label} View`, url: "" },
                ];
            }

                if (path.includes("upload")) {
                return [
                    ...breadcrumbTrail,
                    { label: item.label, url: currentPath },
                    { label: `${item.label} Upload`, url: "" },
                ];
            }

            // Default case: Return the breadcrumb trail without "Edit" or "View"
            return [...breadcrumbTrail, { label: item.label, url: currentPath }];
        }

        // Recursively check for sub-items in nested menus
        if (item.items) {
            const result = findPathInMenu(item.items, path, currentPath, [
                ...breadcrumbTrail,
                { label: item.label, url: currentPath },
            ]);
            if (result.length) return result;
        }
    }
    return [];
};

/**
 * Generates breadcrumbs based on the current path.
 */
const generateBreadcrumbs = (path: string, menuTree: MenuItem[]): { label: string; url: string }[] => {
    const breadcrumbs = findPathInMenu(menuTree, path) || [];

    return breadcrumbs;
};
